import React, { useEffect, useState } from 'react';
import { listGroups, createGroup, deleteGroup, predictGroup } from '../services/groupsClient';

const GroupsPage = () => {
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState('trade1');
  const [symbols, setSymbols] = useState('INFY,TCS,RELIANCE,HDFCBANK,SBIN,ICICIBANK,ITC,TATASTEEL,WIPRO,HCLTECH');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Helper function to determine trend and color
  const getTrendInfo = (forecast) => {
    if (!forecast || forecast.length < 2) return { trend: 'stable', color: '#6b7280', icon: '→' };
    
    const first = forecast[0];
    const last = forecast[forecast.length - 1];
    const change = last - first;
    const changePercent = (change / first) * 100;
    
    if (changePercent > 0.1) return { trend: 'increasing', color: '#10b981', icon: '↗' };
    if (changePercent < -0.1) return { trend: 'decreasing', color: '#ef4444', icon: '↘' };
    return { trend: 'stable', color: '#6b7280', icon: '→' };
  };

  // Helper function to format forecast with colors
  const formatForecast = (forecast) => {
    if (!forecast || forecast.length === 0) return 'N/A';
    
    const trendInfo = getTrendInfo(forecast);
    const formattedValues = forecast
      .filter(v => v != null)
      .map((v, index) => {
        const prevValue = index === 0 ? forecast[0] : forecast[index - 1];
        const isUp = v > prevValue;
        const color = isUp ? '#10b981' : v < prevValue ? '#ef4444' : '#6b7280';
        return `<span style="color: ${color}; font-weight: 500;">${v.toFixed(2)}</span>`;
      })
      .join(', ');
    
    return (
      <span>
        <span style={{ color: trendInfo.color, marginRight: '4px' }}>{trendInfo.icon}</span>
        <span dangerouslySetInnerHTML={{ __html: formattedValues }} />
      </span>
    );
  };

  const refresh = async () => {
    try {
      const res = await listGroups();
      setGroups(res.data || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setBusy(true); setError('');
    try {
      const syms = symbols.split(',').map(s => s.trim()).filter(Boolean);
      await createGroup({ name, symbols: syms });
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const handleDelete = async (id) => {
    setBusy(true);
    try { await deleteGroup(id); await refresh(); } finally { setBusy(false); }
  };

  const handlePredict = async (id, minutes) => {
    setBusy(true); setError(''); setResult(null);
    try {
      const res = await predictGroup(id, minutes);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>📊 Smart Stock Group Management</h1>
          <p style={styles.heroSubtitle}>
            Create, monitor, and analyze curated 10-stock baskets with intelligent workflow automation
          </p>
          <div style={styles.heroFeatures}>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>📦</span>
              <span>10-Stock Baskets</span>
            </div>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>🔄</span>
              <span>Batch Predictions</span>
            </div>
            <div style={styles.featureItem}>
              <span style={styles.featureIcon}>📈</span>
              <span>Real-time Monitoring</span>
            </div>
          </div>
        </div>
        
        {/* What It Does Section */}
        <div style={styles.explanationCard}>
          <h3 style={styles.cardTitle}>How Stock Groups Work</h3>
          <div style={styles.stepsList}>
            <div style={styles.step}>
              <span style={styles.stepNumber}>1</span>
              <div>
                <strong>Create Groups:</strong> Organize up to 10 stocks into named groups (trade1, tech-stocks, etc.)
              </div>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNumber}>2</span>
              <div>
                <strong>Batch Actions:</strong> Run predictions, numerology analysis, or other operations on entire groups
              </div>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNumber}>3</span>
              <div>
                <strong>Monitor Results:</strong> Track group performance with color-coded trends and timestamps
              </div>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNumber}>4</span>
              <div>
                <strong>Quick Actions:</strong> Export data, rename groups, or add from analysis results
              </div>
            </div>
          </div>
          
          <div style={styles.advantagesSection}>
            <h4 style={styles.advantagesTitle}>Key Advantages</h4>
            <div style={styles.advantagesList}>
              <div style={styles.advantage}>⚡ Faster workflow - analyze 10 stocks at once instead of one by one</div>
              <div style={styles.advantage}>🎯 Consistent monitoring - track the same basket over time</div>
              <div style={styles.advantage}>📊 Visual trends - see group performance at a glance</div>
              <div style={styles.advantage}>🔗 Integration - seamlessly connect with numerology and forecasting</div>
            </div>
          </div>
          
          <div style={styles.tipBox}>
            <strong>Pro Tip:</strong> Use groups to organize stocks by sector, strategy, or risk level. Perfect for comparing tech stocks vs banking stocks or high-risk vs stable picks.
          </div>
        </div>
      </div>

      <div className="create-group-section">
        <h3 className="section-title">Create New Group</h3>
        <div className="form-grid">
          <input 
            className="form-input"
            placeholder="Group name (e.g., trade1, tech_stocks)" 
            value={name} 
            onChange={e => setName(e.target.value)} 
          />
          <textarea 
            className="form-textarea"
            rows={2} 
            placeholder="Comma separated symbols (e.g., INFY,TCS,RELIANCE)" 
            value={symbols} 
            onChange={e => setSymbols(e.target.value)} 
          />
          <button 
            className="btn-primary"
            onClick={handleCreate} 
            disabled={busy}
          >
            {busy ? 'Creating...' : 'Create Group'}
          </button>
        </div>
        {error && <div className="error-message">Error: {error}</div>}
      </div>

      <div className="groups-section">
        <h3 className="section-title">Existing Groups</h3>
        <div className="groups-grid">
          {groups.map(g => (
            <div key={g.id} className="group-card">
              <div className="group-header">
                <div className="group-info">
                  <h4 className="group-name">{g.name}</h4>
                  <div className="group-symbols">{g.symbols.join(', ')}</div>
                </div>
                <div className="group-actions">
                  <button 
                    className="btn-predict btn-15m"
                    onClick={() => handlePredict(g.id, 15)} 
                    disabled={busy}
                  >
                    15m
                  </button>
                  <button 
                    className="btn-predict btn-30m"
                    onClick={() => handlePredict(g.id, 30)} 
                    disabled={busy}
                  >
                    30m
                  </button>
                  <button 
                    className="btn-predict btn-60m"
                    onClick={() => handlePredict(g.id, 60)} 
                    disabled={busy}
                  >
                    60m
                  </button>
                  <button 
                    className="btn-predict btn-120m"
                    onClick={() => handlePredict(g.id, 120)} 
                    disabled={busy}
                  >
                    120m
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDelete(g.id)} 
                    disabled={busy}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {result && (
        <div className="prediction-section">
          <h3 className="section-title">
            Prediction Results (next {result.horizon_minutes} minutes)
          </h3>
          <div className="prediction-table-container">
            <table className="prediction-table">
              <thead>
                <tr>
                  <th className="th-symbol">Symbol</th>
                  <th className="th-last">Last Price</th>
                  <th className="th-forecast">Forecast Trend</th>
                  <th className="th-details">5-Minute Intervals</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map(r => {
                  const trendInfo = getTrendInfo(r.forecast);
                  return (
                    <tr key={r.symbol} className="prediction-row">
                      <td className="td-symbol">
                        <span className="symbol-name">{r.symbol}</span>
                      </td>
                      <td className="td-last">
                        <span className="last-price">₹{r.last_price?.toFixed(2)}</span>
                      </td>
                      <td className="td-trend">
                        <span 
                          className="trend-indicator"
                          style={{ color: trendInfo.color }}
                        >
                          {trendInfo.icon} {trendInfo.trend}
                        </span>
                      </td>
                      <td className="td-forecast">
                        {formatForecast(r.forecast)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        .groups-container {
          padding: 24px;
          max-width: 1200px;
          margin: 0 auto;
          background: #f8fafc;
          min-height: 100vh;
        }

        .groups-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .groups-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 8px 0;
        }

        .groups-subtitle {
          font-size: 1.1rem;
          color: #64748b;
          margin: 0;
        }

        .create-group-section, .groups-section, .prediction-section {
          background: white;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 20px 0;
        }

        .form-grid {
          display: grid;
          gap: 16px;
          max-width: 600px;
        }

        .form-input, .form-textarea {
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: #2563eb;
        }

        .btn-primary:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }

        .error-message {
          color: #dc2626;
          background: #fef2f2;
          border: 1px solid #fecaca;
          padding: 12px;
          border-radius: 8px;
          margin-top: 16px;
        }

        .groups-grid {
          display: grid;
          gap: 16px;
        }

        .group-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 20px;
          transition: box-shadow 0.2s;
        }

        .group-card:hover {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .group-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .group-info {
          flex: 1;
          min-width: 200px;
        }

        .group-name {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 8px 0;
        }

        .group-symbols {
          font-size: 0.9rem;
          color: #64748b;
          line-height: 1.4;
        }

        .group-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .btn-predict {
          padding: 8px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-15m { background: #dbeafe; color: #1e40af; }
        .btn-30m { background: #dcfce7; color: #166534; }
        .btn-60m { background: #fef3c7; color: #92400e; }
        .btn-120m { background: #fce7f3; color: #be185d; }

        .btn-predict:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .btn-predict:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-delete {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-delete:hover:not(:disabled) {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .prediction-table-container {
          overflow-x: auto;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .prediction-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
        }

        .prediction-table th {
          background: #f8fafc;
          font-weight: 600;
          color: #374151;
          padding: 16px;
          text-align: left;
          border-bottom: 2px solid #e5e7eb;
        }

        .prediction-table td {
          padding: 16px;
          border-bottom: 1px solid #f3f4f6;
        }

        .prediction-row:hover {
          background: #f8fafc;
        }

        .symbol-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 1.1rem;
        }

        .last-price {
          font-weight: 600;
          color: #1e293b;
          font-size: 1.1rem;
        }

        .trend-indicator {
          font-weight: 600;
          font-size: 1rem;
        }

        .td-forecast {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .groups-container {
            padding: 16px;
          }
          
          .group-header {
            flex-direction: column;
            align-items: stretch;
          }
          
          .group-actions {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    backgroundColor: '#f8fafc',
    minHeight: '100vh'
  },
  heroSection: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    borderRadius: '16px',
    padding: '40px',
    marginBottom: '40px',
    color: 'white'
  },
  heroContent: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  heroTitle: {
    fontSize: '2.5rem',
    fontWeight: '700',
    margin: '0 0 16px 0',
    lineHeight: '1.2'
  },
  heroSubtitle: {
    fontSize: '1.2rem',
    opacity: '0.9',
    maxWidth: '600px',
    margin: '0 auto 30px auto',
    lineHeight: '1.5'
  },
  heroFeatures: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    flexWrap: 'wrap',
    marginBottom: '30px'
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '1rem',
    fontWeight: '500'
  },
  featureIcon: {
    fontSize: '1.2rem'
  },
  explanationCard: {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '30px',
    backdropFilter: 'blur(10px)'
  },
  cardTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    margin: '0 0 20px 0',
    textAlign: 'center'
  },
  stepsList: {
    marginBottom: '30px'
  },
  step: {
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-start',
    marginBottom: '20px',
    padding: '15px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '8px'
  },
  stepNumber: {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    flexShrink: 0
  },
  advantagesSection: {
    marginBottom: '20px'
  },
  advantagesTitle: {
    fontSize: '1.2rem',
    fontWeight: '600',
    margin: '0 0 15px 0'
  },
  advantagesList: {
    display: 'grid',
    gap: '8px'
  },
  advantage: {
    fontSize: '0.95rem',
    opacity: '0.9'
  },
  tipBox: {
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '0.9rem',
    borderLeft: '4px solid rgba(255, 255, 255, 0.3)'
  }
};

export default GroupsPage;


